import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/session';

export const useCredits = () => {
  const uid = useSession((s) => s.session?.user.id);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['credits', uid],
    enabled: !!uid,
    queryFn: async (): Promise<number> => {
      if (!uid) return 0;
      const { data, error } = await supabase
        .from('user_credits')
        .select('credits')
        .eq('user_id', uid)
        .maybeSingle();
      if (error) throw error;
      return data?.credits ?? 0;
    },
  });

  // Realtime subscription: any update to wallet triggers re-render.
  useEffect(() => {
    if (!uid) return;
    const channel = supabase
      .channel(`credits:${uid}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_credits', filter: `user_id=eq.${uid}` },
        () => qc.invalidateQueries({ queryKey: ['credits', uid] }),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [uid, qc]);

  return query;
};
